import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { focusRingClassNames } from './utils';
import { SheetVisibilityContext } from '@/contexts/SheetVisibilityContext/SheetVisibilityContext';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
    return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

/**
 * Reports a sheet as on screen for exactly as long as one is.
 *
 * Rendered inside the portal's content, which is what Radix mounts and unmounts, rather than in
 * the component that returns it: `SheetContent` is an ordinary element its parent renders whether
 * the sheet is open or not, and `Sheet`'s `open` goes false the moment a close is asked for, while
 * the content stays for its 300ms slide-out. Anything that stands down for an open sheet used the
 * second signal and came back on top of a sheet still sliding away.
 */
function SheetVisibilityReporter() {
    const setIsSheetOpen = React.useContext(SheetVisibilityContext)?.setIsSheetOpen;

    React.useEffect(() => {
        if (!setIsSheetOpen) return;

        setIsSheetOpen(true);
        return () => setIsSheetOpen(false);
    }, [setIsSheetOpen]);

    return null;
}

function SheetTrigger({
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
    return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
    return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
    return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
    className,
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
    return (
        <SheetPrimitive.Overlay
            data-slot="sheet-overlay"
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/70',
                className,
            )}
            {...props}
        />
    );
}

function SheetContent({
    className,
    children,
    side = 'right',
    showCloseButton = true,
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
    side?: 'top' | 'right' | 'bottom' | 'left';
    showCloseButton?: boolean;
}) {
    // Separate header/footer elements from content for sticky behavior
    const { headerElements, contentElements, footerElements, hasHeader } = React.useMemo(() => {
        const childArray = React.Children.toArray(children);
        const headers: React.ReactNode[] = [];
        const content: React.ReactNode[] = [];
        const footers: React.ReactNode[] = [];

        childArray.forEach((child) => {
            if (React.isValidElement(child)
                && (child.type === SheetHeader || child.type === SheetDescription)) {
                headers.push(child);
            }
            else if (React.isValidElement(child) && child.type === SheetFooter) {
                footers.push(child);
            }
            else {
                content.push(child);
            }
        });

        return {
            headerElements: headers,
            contentElements: content,
            footerElements: footers,
            hasHeader: headers.length > 0,
        };
    }, [children]);

    return (
        <SheetPortal>
            <SheetOverlay />
            <SheetPrimitive.Content
                data-slot="sheet-content"
                className={cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 overflow-hidden',
                    side === 'right'
                    && 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
                    side === 'left'
                    && 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
                    side === 'top'
                    && 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
                    side === 'bottom'
                    && 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto max-h-[85dvh] border-t',
                    className,
                )}
                {...props}
                aria-modal="true"
            >
                <SheetVisibilityReporter />
                <div className="sticky top-0 z-10 bg-inherit flex-shrink-0">
                    <div
                        className={cn(
                            'relative',
                            showCloseButton && '[&_[data-slot=sheet-title]]:pr-10',
                        )}
                    >
                        {hasHeader && headerElements}
                        {showCloseButton && (
                            <SheetPrimitive.Close
                                className={cn(
                                    'absolute top-6 right-4 rounded-xs outline-none disabled:pointer-events-none',
                                    focusRingClassNames,
                                )}
                            >
                                <XIcon className="size-6 light:text-gz-neutral-800 dark:text-gz-neutral-200" />
                                <span className="sr-only">Close</span>
                            </SheetPrimitive.Close>
                        )}
                    </div>
                </div>
                <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
                    {contentElements}
                </div>
                {footerElements.length > 0 && (
                    <div className="sticky bottom-0 z-10 bg-inherit flex-shrink-0">
                        {footerElements}
                    </div>
                )}
            </SheetPrimitive.Content>
        </SheetPortal>
    );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-header"
            className={cn('flex flex-col gap-1.5 px-4 pt-6 pb-4', className)}
            {...props}
        />
    );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-footer"
            className={cn('mt-auto flex flex-col gap-2 p-4', className)}
            {...props}
        />
    );
}

function SheetTitle({
    className,
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
    return (
        <SheetPrimitive.Title
            data-slot="sheet-title"
            className={cn('text-foreground font-semibold', className)}
            {...props}
        />
    );
}

function SheetDescription({
    className,
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
    return (
        <SheetPrimitive.Description
            data-slot="sheet-description"
            className={cn('text-muted-foreground text-sm', className)}
            {...props}
        />
    );
}

export {
    Sheet,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
};
